import { useState, useMemo } from 'react';
import {
  anniversaryTypeConfigs,
  anniversaries,
  anniversaryStats,
  anniversaryRules,
  getTodayAnniversaries,
  getThisWeekAnniversaries,
  getThisMonthAnniversaries,
  getAnniversariesByType,
  getTypeDistribution,
  getMostCelebrated,
  type AnniversaryEntityType,
  type AnniversaryEntry,
} from '@/mocks/rfwAnniversaries';

type SidebarView = 'hoy' | 'semana' | 'mes' | 'tipos' | 'reglas';
type SortMode = 'felicitaciones' | 'recientes' | 'proximos';

export default function AniversariosPage() {
  const [view, setView] = useState<SidebarView>('hoy');
  const [selectedType, setSelectedType] = useState<AnniversaryEntityType | 'todas'>('todas');
  const [sortMode, setSortMode] = useState<SortMode>('felicitaciones');
  const [expandedAnn, setExpandedAnn] = useState<string | null>(null);
  const [celebratedIds, setCelebratedIds] = useState<Set<string>>(new Set());

  const todayList = useMemo(() => getTodayAnniversaries(), []);
  const weekList = useMemo(() => getThisWeekAnniversaries(), []);
  const monthList = useMemo(() => getThisMonthAnniversaries(), []);
  const typeDist = useMemo(() => getTypeDistribution(), []);
  const topCelebrated = useMemo(() => getMostCelebrated(10), []);

  const activeList = useMemo(() => {
    let list: AnniversaryEntry[] = [];
    if (view === 'hoy') list = todayList;
    else if (view === 'semana') list = weekList;
    else list = monthList;

    if (selectedType !== 'todas') {
      list = list.filter((a) => a.entityType === selectedType);
    }

    if (sortMode === 'felicitaciones') {
      list.sort((a, b) => b.felicitaciones - a.felicitaciones);
    } else if (sortMode === 'recientes') {
      list.sort((a, b) => a.daysUntil - b.daysUntil);
    } else {
      list.sort((a, b) => a.daysUntil - b.daysUntil);
    }
    return list;
  }, [view, selectedType, sortMode, todayList, weekList, monthList]);

  const handleFelicitar = (id: string) => {
    if (celebratedIds.has(id)) return;
    setCelebratedIds((prev) => new Set(prev).add(id));
  };

  return (
    <div className="min-h-screen bg-background-50">
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-rose-500 via-violet-500 to-amber-500 overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/4 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-white/5 rounded-full translate-y-1/3 -translate-x-1/4 blur-3xl"></div>
        <div className="relative max-w-6xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="flex items-center gap-2 text-white/70 text-xs mb-4">
            <a href="/" className="hover:text-white transition-colors">RFW</a>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white">Sistema de Aniversarios</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center">
                  <i className="ri-gift-fill text-2xl text-white"></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-4xl font-bold text-white">Sistema de Aniversarios</h1>
                  <p className="text-white/70 text-sm">Generar cercanía</p>
                </div>
              </div>
              <p className="text-white/80 text-sm md:text-base max-w-lg">
                Radios, programas, locutores, clubes y oyentes que cumplen años en la comunidad. Todo automático, sin configurar nada. Vos solo tenés que felicitar, comentar y compartir.
              </p>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-white">{anniversaryStats.totalToday}</div>
                <div className="text-xs text-white/70">Hoy</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white">{anniversaryStats.totalThisWeek}</div>
                <div className="text-xs text-white/70">Esta Semana</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white">{anniversaryStats.totalFelicitacionesHoy.toLocaleString()}</div>
                <div className="text-xs text-white/70">Felicitaciones Hoy</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Live Pulse Bar */}
      <section className="bg-background-100 border-b border-background-200/70">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3 flex flex-wrap items-center gap-4 md:gap-6 overflow-x-auto">
          <div className="flex items-center gap-2 whitespace-nowrap">
            <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse"></span>
            <span className="text-xs font-semibold text-foreground-700">EN VIVO</span>
          </div>
          {[
            { icon: 'ri-gift-line', color: 'text-rose-600', value: anniversaryStats.totalToday.toString(), label: 'Hoy' },
            { icon: 'ri-calendar-check-line', color: 'text-violet-600', value: anniversaryStats.totalThisWeek.toString(), label: 'Esta Semana' },
            { icon: 'ri-calendar-line', color: 'text-amber-600', value: anniversaryStats.totalThisMonth.toString(), label: 'Este Mes' },
            { icon: 'ri-heart-line', color: 'text-rose-600', value: anniversaryStats.totalFelicitacionesHoy.toLocaleString(), label: 'Felicitaciones' },
            { icon: 'ri-trophy-line', color: 'text-amber-600', value: anniversaryStats.mostCelebratedToday, label: 'Más Felicitado Hoy', long: true },
          ].map((m, i) => (
            <div key={i} className={`flex items-center gap-1.5 whitespace-nowrap text-xs ${m.long ? 'hidden md:flex' : ''}`}>
              <i className={`${m.icon} ${m.color}`}></i>
              <span className="font-semibold text-foreground-950">{m.value}</span>
              <span className="text-foreground-500">{m.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar */}
          <aside className="w-full lg:w-56 flex-shrink-0">
            <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
              {([
                { key: 'hoy', label: 'Hoy', icon: 'ri-gift-line', color: 'text-rose-600', count: todayList.length },
                { key: 'semana', label: 'Esta Semana', icon: 'ri-calendar-check-line', color: 'text-violet-600', count: weekList.length },
                { key: 'mes', label: 'Este Mes', icon: 'ri-calendar-line', color: 'text-amber-600', count: monthList.length },
                { key: 'tipos', label: 'Tipos', icon: 'ri-stack-line', color: 'text-emerald-600', count: 5 },
                { key: 'reglas', label: 'Reglas', icon: 'ri-scales-line', color: 'text-sky-600', count: 3 },
              ] as { key: SidebarView; label: string; icon: string; color: string; count: number }[]).map((item) => (
                <button
                  key={item.key}
                  onClick={() => setView(item.key)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                    view === item.key
                      ? 'bg-rose-50 text-rose-700 border border-rose-200'
                      : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                  }`}
                >
                  <i className={`${item.icon} ${view === item.key ? 'text-rose-600' : ''}`}></i>
                  <span className="flex-1">{item.label}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${view === item.key ? 'bg-rose-100 text-rose-700' : 'bg-background-100 text-foreground-400'}`}>{item.count}</span>
                </button>
              ))}
            </nav>

            {/* Quick Types nav */}
            <div className="hidden lg:block mt-5 pt-5 border-t border-background-200/70">
              <div className="text-xs font-semibold text-foreground-500 mb-2 uppercase tracking-wide">Entidades</div>
              <div className="space-y-1">
                {anniversaryTypeConfigs.map((cfg) => {
                  const count = anniversaries.filter((a) => a.entityType === cfg.key).length;
                  return (
                    <button
                      key={cfg.key}
                      onClick={() => { setView('hoy'); setSelectedType(cfg.key); }}
                      className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs transition-colors cursor-pointer ${
                        selectedType === cfg.key
                          ? `${cfg.bgColor} ${cfg.color}`
                          : 'text-foreground-600 hover:bg-background-100'
                      }`}
                    >
                      <i className={cfg.icon}></i>
                      <span className="flex-1 text-left">{cfg.label}</span>
                      <span className="text-xs opacity-70">{count}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>

          {/* Main Area */}
          <div className="flex-1 min-w-0">
            {/* Views: Hoy, Semana, Mes */}
            {(view === 'hoy' || view === 'semana' || view === 'mes') && (
              <div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5">
                  <h2 className="text-lg font-bold text-foreground-950 flex items-center gap-2">
                    <i className={`${view === 'hoy' ? 'ri-gift-line text-rose-600' : view === 'semana' ? 'ri-calendar-check-line text-violet-600' : 'ri-calendar-line text-amber-600'}`}></i>
                    {view === 'hoy' ? 'Aniversarios de Hoy' : view === 'semana' ? 'Aniversarios de Esta Semana' : 'Aniversarios de Este Mes'}
                    <span className="text-sm font-normal text-foreground-500">({activeList.length} aniversarios)</span>
                  </h2>
                  <div className="flex items-center gap-3">
                    <select
                      value={selectedType}
                      onChange={(e) => setSelectedType(e.target.value === 'todas' ? 'todas' : e.target.value as AnniversaryEntityType)}
                      className="text-xs bg-background-100 border border-background-200/70 rounded-md px-2 py-1.5 text-foreground-700 cursor-pointer"
                    >
                      <option value="todas">Todas las entidades</option>
                      {anniversaryTypeConfigs.map((cfg) => (
                        <option key={cfg.key} value={cfg.key}>{cfg.label}</option>
                      ))}
                    </select>
                    <select
                      value={sortMode}
                      onChange={(e) => setSortMode(e.target.value as SortMode)}
                      className="text-xs bg-background-100 border border-background-200/70 rounded-md px-2 py-1.5 text-foreground-700 cursor-pointer"
                    >
                      <option value="felicitaciones">Más Felicitados</option>
                      <option value="recientes">Más Recientes</option>
                      <option value="proximos">Más Próximos</option>
                    </select>
                  </div>
                </div>

                {/* Today banner */}
                {view === 'hoy' && todayList.length > 0 && (
                  <div className="mb-5 p-5 bg-gradient-to-r from-rose-50 via-violet-50 to-amber-50 border border-rose-200 rounded-xl">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 rounded-xl bg-rose-100 flex items-center justify-center">
                        <i className="ri-gift-fill text-2xl text-rose-500"></i>
                      </div>
                      <div>
                        <div className="font-bold text-base text-rose-700">¡{todayList.length} aniversarios hoy!</div>
                        <div className="text-sm text-rose-600">Andá a felicitar — les va a encantar.</div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {todayList.map((a) => {
                        const cfg = anniversaryTypeConfigs.find((t) => t.key === a.entityType);
                        return (
                          <span key={a.id} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg?.bgColor ?? 'bg-rose-50'} ${cfg?.color ?? 'text-rose-700'}`}>
                            <i className={`${cfg?.icon ?? 'ri-question-line'} text-xs`}></i>
                            {a.entityName}
                            <span className="opacity-70">· {a.years}a</span>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Cards */}
                <div className="space-y-4">
                  {activeList.map((ann) => {
                    const typeCfg = anniversaryTypeConfigs.find((t) => t.key === ann.entityType);
                    const isExpanded = expandedAnn === ann.id;
                    const hasFelicited = celebratedIds.has(ann.id);

                    return (
                      <div
                        key={ann.id}
                        className={`bg-background-50 rounded-lg border transition-all ${
                          ann.isToday ? `${typeCfg?.borderColor ?? 'border-rose-200'}` : 'border-background-200/70'
                        } ${isExpanded ? 'shadow-sm' : ''}`}
                      >
                        {/* Card Header */}
                        <div className="p-4 flex items-center gap-4">
                          {/* Photo */}
                          <div className="relative w-14 h-14 rounded-xl overflow-hidden flex-shrink-0">
                            <img src={ann.entityPhoto} alt={ann.entityName} className="w-full h-full object-cover" />
                            {ann.isToday && (
                              <div className="absolute -top-1 -right-1 w-6 h-6 bg-rose-500 rounded-full border-2 border-background-50 flex items-center justify-center">
                                <i className="ri-gift-fill text-white text-xs"></i>
                              </div>
                            )}
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="font-semibold text-sm text-foreground-950 truncate">{ann.entityName}</span>
                              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${typeCfg?.bgColor ?? 'bg-background-100'} ${typeCfg?.color ?? 'text-foreground-600'}`}>
                                <i className={`${typeCfg?.icon ?? 'ri-question-line'} text-xs`}></i>
                                {typeCfg?.label ?? ann.entityType}
                              </span>
                            </div>
                            <div className="text-xs text-foreground-500 line-clamp-1">{ann.description}</div>
                            <div className="flex items-center gap-2 mt-1 text-xs text-foreground-400">
                              <span>{ann.anniversaryDateFormatted}</span>
                              <span>·</span>
                              <span>Desde {ann.foundedDate.split('-')[0]}</span>
                            </div>
                          </div>

                          {/* Years Badge */}
                          <div className="flex-shrink-0 text-center">
                            <div className={`text-2xl font-bold ${typeCfg?.color ?? 'text-rose-500'}`}>{ann.years}</div>
                            <div className="text-xs text-foreground-400">años</div>
                          </div>

                          {/* Days badge */}
                          <div className="hidden md:flex flex-shrink-0 items-center gap-1.5 text-xs">
                            {ann.isToday ? (
                              <span className="px-2 py-1 bg-rose-100 text-rose-700 rounded-full font-medium">¡Hoy!</span>
                            ) : (
                              <span className="px-2 py-1 bg-background-100 text-foreground-500 rounded-full">
                                {ann.daysUntil === 1 ? 'Mañana' : `En ${ann.daysUntil} días`}
                              </span>
                            )}
                          </div>

                          {/* Action buttons */}
                          <div className="flex-shrink-0 flex items-center gap-1.5">
                            <button
                              onClick={() => handleFelicitar(ann.id)}
                              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all cursor-pointer ${
                                hasFelicited
                                  ? 'bg-rose-100 text-rose-700'
                                  : 'bg-rose-500 text-white hover:bg-rose-600'
                              }`}
                            >
                              <i className={`${hasFelicited ? 'ri-heart-fill' : 'ri-heart-line'} text-xs`}></i>
                              {hasFelicited ? '¡Felicitado!' : 'Felicitar'}
                            </button>
                            <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-400 hover:text-violet-500 hover:bg-violet-50 transition-colors cursor-pointer">
                              <i className="ri-share-forward-line text-sm"></i>
                            </button>
                            <button
                              onClick={() => setExpandedAnn(isExpanded ? null : ann.id)}
                              className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-400 hover:text-sky-500 hover:bg-sky-50 transition-colors cursor-pointer"
                            >
                              <i className={`${isExpanded ? 'ri-arrow-up-s-line' : 'ri-chat-1-line'} text-sm`}></i>
                            </button>
                          </div>
                        </div>

                        {/* Expanded: Comments */}
                        {isExpanded && (
                          <div className="px-4 pb-4 border-t border-background-200/70 pt-4">
                            <div className="text-xs font-semibold text-foreground-500 uppercase tracking-wide mb-3">
                              Comentarios ({ann.comentarios})
                            </div>
                            {ann.comments.length > 0 ? (
                              <div className="space-y-3 mb-4">
                                {ann.comments.map((comment) => (
                                  <div key={comment.id} className="flex items-start gap-3">
                                    <img src={comment.userPhoto} alt={comment.userName} className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs font-semibold text-foreground-950">{comment.userName}</span>
                                        <span className="text-xs text-foreground-400">{comment.dateRelative}</span>
                                      </div>
                                      <p className="text-sm text-foreground-600 mt-0.5">{comment.text}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-center py-4 text-xs text-foreground-400 italic">
                                Sé el primero en comentar
                              </div>
                            )}

                            {/* Quick comment input */}
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-background-100 flex-shrink-0 flex items-center justify-center">
                                <i className="ri-user-line text-foreground-400 text-sm"></i>
                              </div>
                              <input
                                type="text"
                                placeholder={`Felicitar a ${ann.entityName}...`}
                                className="flex-1 text-sm bg-background-100 border border-background-200/70 rounded-full px-4 py-2 text-foreground-700 placeholder:text-foreground-400 focus:outline-none focus:border-rose-300"
                              />
                              <button className="px-3 py-2 bg-rose-500 text-white rounded-full text-xs font-medium hover:bg-rose-600 transition-colors whitespace-nowrap cursor-pointer">
                                Enviar
                              </button>
                            </div>

                            {/* Extra info */}
                            <div className="mt-4 pt-3 border-t border-background-200/70 flex items-center gap-4 text-xs text-foreground-500">
                              <a href={ann.relatedLink} className="flex items-center gap-1 text-rose-600 hover:text-rose-700 font-medium cursor-pointer">
                                <i className="ri-external-link-line"></i>
                                Ver {typeCfg?.label ?? 'entidad'}
                              </a>
                              <span className="flex items-center gap-1">
                                <i className="ri-heart-line text-rose-500"></i>
                                {ann.felicitaciones} felicitaciones
                              </span>
                              <span className="flex items-center gap-1">
                                <i className="ri-share-forward-line text-violet-500"></i>
                                {ann.compartidos} compartidos
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {activeList.length === 0 && (
                    <div className="text-center py-16">
                      <div className="w-20 h-20 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-4">
                        <i className="ri-gift-line text-3xl text-foreground-300"></i>
                      </div>
                      <div className="text-base font-semibold text-foreground-400 mb-2">Sin aniversarios en esta vista</div>
                      <div className="text-sm text-foreground-400">Probá cambiando los filtros o volvé más tarde.</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* View: Tipos */}
            {view === 'tipos' && (
              <div>
                <h2 className="text-lg font-bold text-foreground-950 flex items-center gap-2 mb-5">
                  <i className="ri-stack-line text-emerald-600"></i>
                  Tipos de Aniversario
                </h2>
                <div className="space-y-4">
                  {anniversaryTypeConfigs.map((cfg) => {
                    const itemsOfType = anniversaries.filter((a) => a.entityType === cfg.key);
                    const todayCount = itemsOfType.filter((a) => a.isToday).length;
                    return (
                      <div key={cfg.key} className={`bg-background-50 rounded-lg border ${cfg.borderColor} overflow-hidden`}>
                        <div className={`p-4 bg-gradient-to-r ${cfg.gradient} text-white`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                                <i className={`${cfg.icon} text-2xl`}></i>
                              </div>
                              <div>
                                <div className="font-bold text-lg">{cfg.label}</div>
                                <div className="text-sm text-white/80">{cfg.description}</div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-bold">{itemsOfType.length}</div>
                              <div className="text-sm text-white/70">{todayCount > 0 ? `${todayCount} hoy` : 'aniversarios'}</div>
                            </div>
                          </div>
                        </div>
                        {itemsOfType.length > 0 && (
                          <div className="p-4">
                            <div className="text-xs font-semibold text-foreground-500 uppercase tracking-wide mb-3">
                              {cfg.celebrationVerb === 'cumple' ? `Cumplen años ${cfg.label.toLowerCase()}s` : cfg.celebrationVerb}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {itemsOfType.map((item) => (
                                <a key={item.id} href={item.relatedLink} className="flex items-center gap-3 p-2.5 bg-background-100 rounded-lg hover:bg-rose-50 transition-colors cursor-pointer">
                                  <img src={item.entityPhoto} alt={item.entityName} className="w-10 h-10 rounded-lg object-cover" />
                                  <div className="flex-1 min-w-0">
                                    <div className="text-xs font-medium text-foreground-950 truncate">{item.entityName}</div>
                                    <div className="text-xs text-foreground-500">{item.years} años · {item.anniversaryDateFormatted}</div>
                                  </div>
                                  {item.isToday && (
                                    <span className="px-1.5 py-0.5 bg-rose-100 text-rose-700 rounded-full text-xs font-medium">Hoy</span>
                                  )}
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* View: Reglas */}
            {view === 'reglas' && (
              <div>
                <h2 className="text-lg font-bold text-foreground-950 flex items-center gap-2 mb-5">
                  <i className="ri-scales-line text-sky-600"></i>
                  Reglas del Sistema
                </h2>
                <div className="space-y-4 mb-6">
                  {anniversaryRules.map((rule) => (
                    <div key={rule.id} className="bg-background-50 rounded-lg p-5 border border-background-200/70">
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-xl ${rule.bgColor} flex items-center justify-center flex-shrink-0`}>
                          <i className={`${rule.icon} text-lg ${rule.color}`}></i>
                        </div>
                        <div>
                          <div className="font-semibold text-base text-foreground-950 mb-2">{rule.title}</div>
                          <div className="text-sm text-foreground-600 leading-relaxed">{rule.description}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Auto detection example */}
                <div className="bg-rose-50 rounded-lg p-5 border border-rose-200">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-sparkling-line text-lg text-rose-600"></i>
                    </div>
                    <div>
                      <div className="font-semibold text-sm text-rose-800 mb-2">Ejemplo: Detección Automática en Acción</div>
                      <div className="text-sm text-rose-700 leading-relaxed">
                        El 21 de Junio a las 00:00, el sistema detectó automáticamente 5 aniversarios: La Mega 107.3 FM (18 años), Carrusel Deportivo (10 años), Alejandro "El Monstruo" Ramírez (15 años al aire), Club Fans La Mega (6 años), y María José Herrera (4 años en RFW). Sin intervención manual. Los oyentes recibieron notificaciones suaves solo si seguían a esas entidades. En las primeras 6 horas, ya se registraron {anniversaryStats.totalFelicitacionesHoy.toLocaleString()} felicitaciones. Todo automático.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Cross Links */}
        <div className="mt-10 pt-8 border-t border-background-200/70">
          <div className="text-xs font-semibold text-foreground-500 uppercase tracking-wide mb-3">También te puede interesar</div>
          <div className="flex flex-wrap gap-2">
            {[
              { href: '/eventos', label: 'Eventos', icon: 'ri-calendar-event-line' },
              { href: '/clubes', label: 'Clubes', icon: 'ri-team-line' },
              { href: '/favoritos', label: 'Favoritos', icon: 'ri-heart-line' },
              { href: '/rachas', label: 'Rachas', icon: 'ri-fire-line' },
              { href: '/centro-social', label: 'Centro Social', icon: 'ri-global-line' },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-background-100 rounded-full text-xs text-foreground-600 hover:bg-rose-50 hover:text-rose-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className={link.icon}></i>
                {link.label}
              </a>
            ))}
          </div>
        </div>

        {/* Final Rule */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-rose-50 border border-rose-200 rounded-full">
            <i className="ri-sparkling-line text-rose-600"></i>
            <span className="text-sm font-semibold text-rose-700">Automático.</span>
          </div>
        </div>
      </div>
    </div>
  );
}